/**
 * Tests Check Module
 * Detects missing tests and test quality issues
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Patterns to identify testable components
const ENTITY_PATTERN = /public\s+class\s+(\w+)\s*:\s*(Base|System)Entity/gi;
const SERVICE_PATTERN = /public\s+class\s+(\w+Service)\s*:\s*I\w+Service/gi;
const CONTROLLER_PATTERN = /public\s+class\s+(\w+Controller)\s*:\s*ControllerBase/gi;
const VALIDATOR_PATTERN = /public\s+class\s+(\w+Validator)\s*:\s*AbstractValidator/gi;

// Test quality patterns
const USELESS_ASSERTION_PATTERNS = [
  /Assert\.True\s*\(\s*true\s*\)/gi,
  /Assert\.False\s*\(\s*false\s*\)/gi,
  /\.Should\(\)\.BeTrue\(\)/gi,
  /expect\s*\(\s*true\s*\)\s*\.toBe\s*\(\s*true\s*\)/gi,
  /expect\s*\(\s*1\s*\)\s*\.toBe\s*\(\s*1\s*\)/gi,
];

// NO_ASSERTION_PATTERN reserved for future use: /\[Fact\][\s\S]*?public\s+(?:async\s+)?(?:Task|void)\s+\w+\s*\([^)]*\)\s*\{[^}]*\}/gi

export async function checkTests(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  // Collect all testable components
  const entities = new Set<string>();
  const services = new Set<string>();
  const controllers = new Set<string>();
  const validators = new Set<string>();

  // Collect existing tests
  const existingTests = new Set<string>();

  for (const file of context.files) {
    if (file.language !== 'csharp') continue;

    // Identify components
    let match;

    ENTITY_PATTERN.lastIndex = 0;
    while ((match = ENTITY_PATTERN.exec(file.content)) !== null) {
      entities.add(match[1]);
    }

    SERVICE_PATTERN.lastIndex = 0;
    while ((match = SERVICE_PATTERN.exec(file.content)) !== null) {
      services.add(match[1]);
    }

    CONTROLLER_PATTERN.lastIndex = 0;
    while ((match = CONTROLLER_PATTERN.exec(file.content)) !== null) {
      controllers.add(match[1]);
    }

    VALIDATOR_PATTERN.lastIndex = 0;
    while ((match = VALIDATOR_PATTERN.exec(file.content)) !== null) {
      validators.add(match[1]);
    }

    // Identify test files
    if (file.relativePath.includes('Tests') || file.relativePath.includes('.test.')) {
      // Extract tested component names
      const testClassMatch = /public\s+class\s+(\w+)Tests/gi.exec(file.content);
      if (testClassMatch) {
        existingTests.add(testClassMatch[1]);
      }

      // Check test quality
      checkTestQuality(file, findings);
    }
  }

  // Check for missing tests
  checkMissingTests(entities, existingTests, 'Entity', findings);
  checkMissingTests(services, existingTests, 'Service', findings);
  checkMissingTests(controllers, existingTests, 'Controller', findings);
  checkMissingTests(validators, existingTests, 'Validator', findings);

  return findings;
}

function checkMissingTests(
  components: Set<string>,
  existingTests: Set<string>,
  type: string,
  findings: CodeReviewFinding[]
): void {
  for (const component of components) {
    // Normalize name for comparison
    const baseName = component.replace(/(Service|Controller|Validator)$/, '');

    const hasTest =
      existingTests.has(component) ||
      existingTests.has(baseName) ||
      existingTests.has(`${baseName}${type}`);

    if (!hasTest) {
      const priority = getPriority(type);

      findings.push({
        id: generateFindingId('tests'),
        category: 'tests',
        severity: priority === 'critical' ? 'critical' : 'warning',
        title: `Missing Tests for ${component}`,
        description: `No test file found for ${type.toLowerCase()} ${component}. ${type}s should have comprehensive test coverage.`,
        file: `Tests/Unit/${type}s/${component}Tests.cs`,
        suggestion: `Create a test file: Tests/Unit/${type}s/${component}Tests.cs`,
        autoFixable: false,
      });
    }
  }
}

function checkTestQuality(file: { relativePath: string; content: string }, findings: CodeReviewFinding[]): void {
  // Check for useless assertions
  for (const pattern of USELESS_ASSERTION_PATTERNS) {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);

      findings.push({
        id: generateFindingId('tests'),
        category: 'tests',
        severity: 'warning',
        title: 'Useless Test Assertion',
        description: 'This assertion always passes and doesn\'t test any behavior.',
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(match[0]),
        suggestion: 'Replace with meaningful assertions that verify actual behavior.',
        autoFixable: false,
      });
    }
  }

  // Check for tests without assertions (simplified check)
  const factMatches = file.content.matchAll(/\[Fact\]\s*\n\s*public\s+(?:async\s+)?(?:Task|void)\s+(\w+)/gi);
  for (const match of factMatches) {
    const methodName = match[1];
    const methodStart = match.index || 0;

    // Find method body (simplified)
    const bodyStart = file.content.indexOf('{', methodStart);
    const bodyEnd = findMatchingBrace(file.content, bodyStart);

    if (bodyStart !== -1 && bodyEnd !== -1) {
      const methodBody = file.content.slice(bodyStart, bodyEnd);

      // Check if body has assertions
      const hasAssertion =
        /Assert\./i.test(methodBody) ||
        /\.Should\(/i.test(methodBody) ||
        /expect\s*\(/i.test(methodBody) ||
        /Verify\s*\(/i.test(methodBody);

      if (!hasAssertion) {
        const lineNumber = getLineNumber(file.content, methodStart);

        findings.push({
          id: generateFindingId('tests'),
          category: 'tests',
          severity: 'warning',
          title: 'Test Without Assertions',
          description: `Test method ${methodName} does not appear to have any assertions.`,
          file: file.relativePath,
          line: lineNumber,
          suggestion: 'Add assertions to verify the expected behavior. A test without assertions doesn\'t prove anything.',
          autoFixable: false,
        });
      }
    }
  }
}

function getPriority(type: string): 'critical' | 'high' | 'medium' | 'low' {
  switch (type) {
    case 'Service':
      return 'critical';
    case 'Controller':
      return 'high';
    case 'Validator':
      return 'high';
    case 'Entity':
      return 'medium';
    default:
      return 'low';
  }
}

function findMatchingBrace(content: string, start: number): number {
  let depth = 0;
  for (let i = start; i < content.length; i++) {
    if (content[i] === '{') depth++;
    if (content[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
